import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';

import { MaskitoElementPredicate, MaskitoOptions } from '@maskito/core';
import { select, Store } from '@ngrx/store';

import { inRange, isEqual, isNull, round } from 'lodash-es';
import { BehaviorSubject, Observable, throttleTime } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import { FORM_SECTION } from '../../../../../constants/form.constant';
import {
  amountMask,
  cleanUpNullishMaskValue,
  rateAmountMask,
  rateAmountSymbolRegExPattern,
  rateMask
} from '../../../../../constants/maskito.constants';
import { showComponentByFieldsVisibility } from '../../../../../helpers/form';
import * as fromNetSheetActions from '../../../../../store/actions/net-sheet.actions';
import * as fromNetSheetSelectors from '../../../../../store/selectors/net-sheet.selectors';
import { FormSection } from '../../../../../typings/form';
import {
  NetSheetFormFieldSettings,
  NetSheetSalesCommissionBrokerCommissionTotalSettings
} from '../../../../../typings/net-sheet';
import { GenericComponent } from '../../../../components/generic/generic.component';

interface FormModel {
  salesCommissionBrokerCommissionListing: FormControl<string | null>;
  salesCommissionBrokerCommissionSelling: FormControl<string | null>;
  salesCommissionBrokerCommissionSplits: FormControl<boolean>;
  salesCommissionBrokerCommissionTaxListing: FormControl<boolean>;
  salesCommissionBrokerCommissionTaxSelling: FormControl<boolean>;
  salesCommissionBrokerCommissionTaxTotal: FormControl<boolean>;
  salesCommissionBrokerCommissionTierRate: FormControl<string | null>;
  salesCommissionBrokerCommissionTotal: FormControl<string | null>;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: FORM_SECTION,
      useExisting: SalesCommissionComponent
    }
  ],
  selector: 'red-net-sheet-sales-commission',
  standalone: false,
  templateUrl: './sales-commission.component.html'
})
export class SalesCommissionComponent extends GenericComponent implements OnInit, FormSection {
  private store = inject(Store);

  showComponent$ = new BehaviorSubject(true);

  form = new FormGroup<FormModel>({
    salesCommissionBrokerCommissionListing: new FormControl<string | null>(null),
    salesCommissionBrokerCommissionSelling: new FormControl<string | null>(null),
    salesCommissionBrokerCommissionSplits: new FormControl<boolean>(false, {
      nonNullable: true
    }),
    salesCommissionBrokerCommissionTaxListing: new FormControl<boolean>(false, {
      nonNullable: true
    }),
    salesCommissionBrokerCommissionTaxSelling: new FormControl<boolean>(false, {
      nonNullable: true
    }),
    salesCommissionBrokerCommissionTaxTotal: new FormControl<boolean>(false, {
      nonNullable: true
    }),
    salesCommissionBrokerCommissionTierRate: new FormControl<string | null>(null),
    salesCommissionBrokerCommissionTotal: new FormControl<string | null>(null)
  });

  brokerCommissionTotalSettings$: Observable<NetSheetSalesCommissionBrokerCommissionTotalSettings | null>;

  fieldsSettings$ = new BehaviorSubject<{ [key: string]: NetSheetFormFieldSettings } | null>(null);

  constructor() {
    super();

    this.form.controls.salesCommissionBrokerCommissionTotal.valueChanges
      .pipe(throttleTime(200, undefined, { leading: true, trailing: true }))
      .subscribe(() => this.updateFromTotalToSplits());

    this.form.controls.salesCommissionBrokerCommissionListing.valueChanges
      .pipe(throttleTime(200, undefined, { leading: true, trailing: true }))
      .subscribe(() => this.updateFromSplitsToTotal());

    this.form.controls.salesCommissionBrokerCommissionSelling.valueChanges
      .pipe(throttleTime(200, undefined, { leading: true, trailing: true }))
      .subscribe(() => this.updateFromSplitsToTotal());

    this.form.valueChanges.subscribe((values) => this.updateValues(values));

    this.brokerCommissionTotalSettings$ = this.store.pipe(
      select(fromNetSheetSelectors.selectSalesCommissionsBrokerCommissionTotalSettings),
      throttleTime(200, undefined, { leading: true, trailing: true }),
      distinctUntilChanged((previous, current) => isEqual(previous, current))
    );
  }

  updateFromTotalToSplits() {
    const brokerCommissionTotal = cleanUpNullishMaskValue(
      this.form.controls.salesCommissionBrokerCommissionTotal.value,
      rateAmountSymbolRegExPattern
    );
    if (!isNull(brokerCommissionTotal)) {
      let splitValues = round(brokerCommissionTotal / 2, 2);
      if (inRange(brokerCommissionTotal, 0, 21)) {
        this.form.controls.salesCommissionBrokerCommissionListing.patchValue(`${splitValues}%`, { emitEvent: false });
        this.form.controls.salesCommissionBrokerCommissionSelling.patchValue(`${splitValues}%`, { emitEvent: false });
      } else {
        this.form.controls.salesCommissionBrokerCommissionListing.patchValue(`$${splitValues}`, { emitEvent: false });
        this.form.controls.salesCommissionBrokerCommissionSelling.patchValue(`$${splitValues}`, { emitEvent: false });
      }
      this.updateValues(this.form.value);
    }
  }

  updateFromSplitsToTotal() {
    const brokerCommissionListing = cleanUpNullishMaskValue(
      this.form.controls.salesCommissionBrokerCommissionListing.value,
      rateAmountSymbolRegExPattern
    );
    const brokerCommissionSelling = cleanUpNullishMaskValue(
      this.form.controls.salesCommissionBrokerCommissionSelling.value,
      rateAmountSymbolRegExPattern
    );
    if (!isNull(brokerCommissionListing) && !isNull(brokerCommissionSelling)) {
      const total = brokerCommissionListing + brokerCommissionSelling;
      const isListingRate = inRange(brokerCommissionListing, 0, 21);
      const isSellingRate = inRange(brokerCommissionSelling, 0, 21);
      const isTotalRate = inRange(total, 0, 21);
      if (isListingRate && isSellingRate && isTotalRate) {
        this.form.controls.salesCommissionBrokerCommissionTotal.patchValue(`${total}%`, {
          emitEvent: false
        });
      } else if (!isListingRate && !isSellingRate && !isTotalRate) {
        this.form.controls.salesCommissionBrokerCommissionTotal.patchValue(`$${total}`, {
          emitEvent: false
        });
      } else {
        this.form.controls.salesCommissionBrokerCommissionTotal.patchValue(null, {
          emitEvent: false
        });
      }
      this.updateValues(this.form.value);
    }
  }

  ngOnInit() {
    this.addUniqueSubscription(
      'fieldsDataSubscription',
      this.store
        .pipe(
          select(fromNetSheetSelectors.selectSalesCommissionFieldsData),
          throttleTime(200, undefined, { leading: true, trailing: true }),
          distinctUntilChanged((previous, current) => isEqual(previous, current))
        )
        .subscribe((data) => {
          this.form.patchValue(
            {
              ...data
            },
            { emitEvent: false }
          );
          this.valid.set(this.form.valid);
        })
    );

    this.addUniqueSubscription(
      'fieldsSettingsSubscription',
      this.store
        .pipe(
          select(fromNetSheetSelectors.selectSalesCommissionFieldsSettings),
          throttleTime(200, undefined, { leading: true, trailing: true }),
          distinctUntilChanged((previous, current) => isEqual(previous, current))
        )
        .subscribe((fields) => {
          this.showComponent$.next(showComponentByFieldsVisibility(fields));
          this.fieldsSettings$.next(fields);
          setTimeout(() => {
            this.valid.set(this.form.valid);
          });
        })
    );
  }

  private updateValues(
    values: Partial<{
      salesCommissionBrokerCommissionListing: string | null;
      salesCommissionBrokerCommissionSelling: string | null;
      salesCommissionBrokerCommissionSplits: boolean;
      salesCommissionBrokerCommissionTaxListing: boolean;
      salesCommissionBrokerCommissionTaxSelling: boolean;
      salesCommissionBrokerCommissionTaxTotal: boolean;
      salesCommissionBrokerCommissionTierRate: string | null;
      salesCommissionBrokerCommissionTotal: string | null;
    }>
  ) {
    this.store.dispatch(
      fromNetSheetActions.updateSalesCommissions({
        payload: {
          ...values
        }
      })
    );
    this.valid.set(this.form.valid);
  }

  readonly rateMask: MaskitoOptions = rateMask;
  readonly amountMask: MaskitoOptions = amountMask;
  readonly rateAmountMask: MaskitoOptions = rateAmountMask(20);
  readonly maskPredicate: MaskitoElementPredicate = async (el) =>
    (el as unknown as HTMLIonInputElement).getInputElement();
  readonly valid = signal(this.form.valid);
}
